import { supabase } from '@/integrations/supabase/client';
import {
  getPremiumEntitlements,
  invalidatePremiumEntitlementsCache,
  isPurchasesAvailable,
  purchasePremium,
  restorePremiumPurchases,
  type PremiumProductId,
  type StoreTransaction,
} from '@/lib/straivPurchases';

const ENTITLEMENT_SYNC_TTL_MS = 10 * 60 * 1000;
let lastEntitlementSyncAt = 0;
let entitlementSyncInflight: Promise<unknown> | null = null;

async function verifyTransactions(transactions: StoreTransaction[]) {
  const jwsRepresentations = transactions
    .map((t) => t.jwsRepresentation)
    .filter((j): j is string => typeof j === 'string' && j.length > 20);

  if (jwsRepresentations.length === 0) {
    return { ok: false as const, premium: false, message: 'No signed transactions to verify.' };
  }

  const { data, error } = await supabase.functions.invoke('verify-apple-subscription', {
    body: { jwsRepresentations },
  });

  if (error) throw error;
  if (data?.error) {
    throw new Error(data.message || data.error);
  }
  return data as {
    ok: boolean;
    premium: boolean;
    productId?: string;
    expiresAt?: string | null;
    message?: string;
  };
}

export async function buyPremiumAndSync(productId: PremiumProductId | string) {
  if (!isPurchasesAvailable()) {
    throw new Error('Purchases are only available in the iOS app.');
  }
  const result = await purchasePremium(productId);
  if (result.cancelled) return { cancelled: true as const, premium: false };
  if (result.pending) return { pending: true as const, premium: false };
  if (!result.success || !result.transaction) {
    throw new Error('Purchase did not complete.');
  }
  const verified = await verifyTransactions([result.transaction]);
  invalidatePremiumEntitlementsCache();
  lastEntitlementSyncAt = 0;
  return { cancelled: false as const, pending: false as const, ...verified };
}

export async function restorePremiumAndSync() {
  if (!isPurchasesAvailable()) {
    throw new Error('Restore is only available in the iOS app.');
  }
  invalidatePremiumEntitlementsCache();
  lastEntitlementSyncAt = 0;
  const { entitlements } = await restorePremiumPurchases();
  if (!entitlements?.length) {
    const current = await getPremiumEntitlements(true);
    if (!current.length) {
      return { ok: true, premium: false, message: 'No purchases to restore.' };
    }
    return verifyTransactions(current);
  }
  return verifyTransactions(entitlements);
}

export async function syncExistingEntitlements() {
  if (!isPurchasesAvailable()) return null;
  if (entitlementSyncInflight) return entitlementSyncInflight;
  if (lastEntitlementSyncAt > 0 && Date.now() - lastEntitlementSyncAt < ENTITLEMENT_SYNC_TTL_MS) {
    return null;
  }
  const run = (async () => {
    const entitlements = await getPremiumEntitlements();
    lastEntitlementSyncAt = Date.now();
    if (!entitlements.length) return null;
    return verifyTransactions(entitlements);
  })().finally(() => {
    if (entitlementSyncInflight === run) entitlementSyncInflight = null;
  });
  entitlementSyncInflight = run;
  return run;
}
